/**
 * Scene, renderer, lights, and the angled top-down follow camera.
 *
 * The camera floats above and slightly toward +Z from the focus point and looks
 * down at it, so the arena reads like the Wii Play Tanks overhead view. A soft
 * directional sun casts shadows; its shadow frustum follows the focus.
 */

import * as THREE from "three";
import { CAMERA } from "../config";

// ---- day/night cycle: keyframe "stops" lerped over the game clock ----
// `t` ∈ [0,1) = fraction of one day. Dawn/dusk bands are WIDE (long golden hours →
// long shadows linger); noon is a short plateau; night is shallow (NOT super dark).
interface SkyStop {
  t: number;
  sun: number; sunI: number;            // directional sun
  amb: number; ambI: number;            // ambient (night floor keeps it lit)
  hemiS: number; hemiG: number; hemiI: number; // hemisphere
  fog: number; skyTop: number; skyBot: number; // fog + recolorable gradient sky
  cloud: number;                        // cloud tint (unlit billboards → tinted by phase so they don't glow at night)
  elev: number;                         // sun elevation (rad) — low = long shadows
}
const DAY: SkyStop[] = [
  { t: 0.00, sun: 0x9fb6e8, sunI: 0.52, amb: 0x6677b8, ambI: 0.62, hemiS: 0x2a3a66, hemiG: 0x14110d, hemiI: 0.5, fog: 0x2b3354, skyTop: 0x0b1330, skyBot: 0x243a6a, cloud: 0x46506e, elev: 0.24 }, // night (moonlit, readable)
  { t: 0.12, sun: 0xffb066, sunI: 0.9, amb: 0x9a96be, ambI: 0.74, hemiS: 0x9fb0d8, hemiG: 0x2a2018, hemiI: 0.62, fog: 0xc99a72, skyTop: 0x2a4a8a, skyBot: 0xe6a766, cloud: 0xd7a079, elev: 0.11 }, // dawn (long golden)
  { t: 0.25, sun: 0xffd8a0, sunI: 1.0, amb: 0xc4ccea, ambI: 0.85, hemiS: 0xbcd2f0, hemiG: 0x2f2820, hemiI: 0.68, fog: 0xcdb48f, skyTop: 0x4a86c0, skyBot: 0xbcd2f0, cloud: 0xdde7f1, elev: 0.5 }, // morning
  { t: 0.42, sun: 0xfff2d6, sunI: 1.05, amb: 0xffffff, ambI: 0.9, hemiS: 0xcfe4ff, hemiG: 0x2a2620, hemiI: 0.7, fog: 0xaea088, skyTop: 0x3a6ea8, skyBot: 0x8fb8de, cloud: 0xeef3f8, elev: 1.15 }, // NOON (the verified day look)
  { t: 0.6, sun: 0xffe6c0, sunI: 1.02, amb: 0xeceaf0, ambI: 0.88, hemiS: 0xcfe0f4, hemiG: 0x2c2620, hemiI: 0.69, fog: 0xc2ae8c, skyTop: 0x3f72ab, skyBot: 0xb6c2cf, cloud: 0xe6ecf1, elev: 0.62 }, // afternoon
  { t: 0.78, sun: 0xff8a55, sunI: 0.82, amb: 0x9a8ab0, ambI: 0.74, hemiS: 0x8a7fb0, hemiG: 0x261c14, hemiI: 0.6, fog: 0xb06a4a, skyTop: 0x2a3a72, skyBot: 0xc2643a, cloud: 0xcf8866, elev: 0.1 }, // dusk (long golden)
  { t: 0.9, sun: 0xb98fc0, sunI: 0.6, amb: 0x7676aa, ambI: 0.64, hemiS: 0x3a4470, hemiG: 0x1a1610, hemiI: 0.52, fog: 0x4a4368, skyTop: 0x141d44, skyBot: 0x3a356a, cloud: 0x5a5480, elev: 0.18 }, // twilight → night
];
const DAY_LENGTH = 300; // game-clock seconds per full day/night cycle
const SUN_HDIR = new THREE.Vector2(120, 140).normalize(); // fixed sun azimuth (shadows keep one direction)
const smoothstep = (x: number) => x * x * (3 - 2 * x);
const MAX_CLOUDS = 120; // pool cap (count is scaled per layer up to this)
const CLOUD_AREA = 230000; // ~one cloud per ~480×480 studs of covered sky
const _cm = new THREE.Matrix4(), _cv = new THREE.Vector3(), _cs = new THREE.Vector3(), _cq = new THREE.Quaternion(), _ce = new THREE.Euler();

export class World {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  domElement: HTMLCanvasElement;

  private sun: THREE.DirectionalLight;
  private ambient!: THREE.AmbientLight; // promoted to a field so day/night can modulate it
  private hemi!: THREE.HemisphereLight;
  private camAim = new THREE.Vector3();
  private want = new THREE.Vector3();
  private ray = new THREE.Raycaster();
  private ndc = new THREE.Vector2();
  // day/night + clouds
  private sunBase = new THREE.Vector3(120, 260, 140); // sun offset from focus (set by time-of-day)
  // clouds: WORLD-ANCHORED (do NOT ride the player) — one InstancedMesh, count scaled to the map size,
  // spread over the whole floor + a margin (so a zoomed-out view still has sky cover).
  private clouds?: THREE.InstancedMesh;
  private cloudMat?: THREE.MeshBasicMaterial; // tinted by the day/night cycle so clouds don't glow at night
  private cloudData: { x: number; z: number; w: number; h: number; spin: number }[] = [];
  private cloudCount = 0;
  private cloudExtX = 1000; // half-extent the clouds tile across (set per layer); drift wraps at ±this
  private cloudExtZ = 1000;
  private skyCtx?: CanvasRenderingContext2D; // recolorable gradient sky (redrawn per cycle)
  private skyTex?: THREE.CanvasTexture;
  private _cA = new THREE.Color(); // lerp scratch (no per-frame alloc)
  private _cB = new THREE.Color();

  constructor(container: HTMLElement) {
    this.scene = new THREE.Scene();
    // a real sky beyond the glass walls instead of black void (Roblox skybox,
    // shared with the zombies game). Fog tinted to the sky's horizon so the
    // receding lower tower layers melt into the sky rather than into darkness.
    this.loadSkybox();
    this.scene.fog = new THREE.Fog(0xaea088, 460, 1040); // matches the skybox horizon

    this.camera = new THREE.PerspectiveCamera(CAMERA.fov, window.innerWidth / window.innerHeight, 10, 6500); // near raised + far extended for max zoom-out depth range
    this.camera.position.set(0, CAMERA.height, CAMERA.back);
    this.camera.lookAt(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    container.appendChild(this.renderer.domElement);
    this.domElement = this.renderer.domElement;

    this.ambient = new THREE.AmbientLight(0xffffff, 0.9);
    this.scene.add(this.ambient);
    this.hemi = new THREE.HemisphereLight(0xcfe4ff, 0x2a2620, 0.7);
    this.scene.add(this.hemi);

    this.sun = new THREE.DirectionalLight(0xfff2d6, 1.05);
    this.sun.position.set(120, 260, 140);
    this.sun.castShadow = true;
    // 1024² shadow map; widened ±260 follow-frustum (far 900) so the LONG golden-hour
    // shadows (low sun) don't clip at the frustum edge as the sun's elevation drops.
    this.sun.shadow.mapSize.set(1024, 1024);
    const sc = this.sun.shadow.camera;
    sc.left = -260; sc.right = 260; sc.top = 260; sc.bottom = -260; sc.near = 1; sc.far = 900;
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.8;
    this.scene.add(this.sun, this.sun.target);

    this.buildClouds();
    window.addEventListener("resize", this.onResize);
  }

  private lastSkyTop = -1;
  private lastSkyBot = -1;

  // The sky is a RECOLORABLE vertical gradient (the day/night cycle tints zenith→horizon). The
  // Roblox cube map can't be recolored, so it's retired in favor of the cycle; the drifting
  // clouds + golden-hour tints add the visual interest back. (Cube helpers kept below, unused.)
  private loadSkybox(): void {
    const c = document.createElement("canvas");
    c.width = 16; c.height = 256;
    this.skyCtx = c.getContext("2d")!;
    this.skyTex = new THREE.CanvasTexture(c);
    this.skyTex.colorSpace = THREE.SRGBColorSpace;
    this.scene.background = this.skyTex;
    this.setSky(0x3a6ea8, 0x8fb8de); // initial daytime gradient (overwritten by setTimeOfDay)
  }

  /** Recolor the gradient sky: zenith `top` → horizon `bot` (both 0xRRGGBB). One tiny canvas redraw. */
  private setSky(top: number, bot: number): void {
    if (!this.skyCtx || !this.skyTex) return;
    const hex = (n: number) => "#" + n.toString(16).padStart(6, "0");
    const g = this.skyCtx.createLinearGradient(0, 0, 0, 256);
    g.addColorStop(0, hex(top));
    g.addColorStop(1, hex(bot));
    this.skyCtx.fillStyle = g;
    this.skyCtx.fillRect(0, 0, 16, 256);
    this.skyTex.needsUpdate = true;
  }

  /** Build the cloud pool: ONE InstancedMesh of soft flat sprites (see-through, no shadows). World-anchored
   *  (laid out per layer by layoutClouds); a single draw call regardless of how big the floor gets. */
  private buildClouds(): void {
    const c = document.createElement("canvas"); c.width = c.height = 128;
    const x = c.getContext("2d")!;
    const g = x.createRadialGradient(64, 64, 4, 64, 64, 62);
    g.addColorStop(0, "rgba(255,255,255,0.95)"); g.addColorStop(0.5, "rgba(255,255,255,0.45)"); g.addColorStop(1, "rgba(255,255,255,0)");
    x.fillStyle = g; x.fillRect(0, 0, 128, 128);
    const tex = new THREE.CanvasTexture(c);
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.6, depthWrite: false, fog: true });
    this.cloudMat = mat; // setTimeOfDay tints this so clouds darken at night instead of glowing
    const geom = new THREE.PlaneGeometry(1, 1);
    this.clouds = new THREE.InstancedMesh(geom, mat, MAX_CLOUDS); // coplanar → no per-instance sort needed
    this.clouds.count = 0;
    this.clouds.frustumCulled = false; // covers the whole map; never cull the whole batch
    this.scene.add(this.clouds);
  }

  /** Lay out the cloud field for a floor of half-extent (halfX,halfZ) at world height `floorY`. Clouds are
   *  WORLD-FIXED (don't move with the player), spread over the map + a margin (visible when zoomed out),
   *  with the COUNT scaled to the covered area (capped) so it works from layer 1 to a layer-2000 map. */
  layoutClouds(halfX: number, halfZ: number, floorY: number): void {
    if (!this.clouds) return;
    const M = 420; // margin past the map edge so a zoomed-out view still has sky cover
    this.cloudExtX = halfX + M;
    this.cloudExtZ = halfZ + M;
    const area = 2 * this.cloudExtX * 2 * this.cloudExtZ;
    this.cloudCount = Math.max(18, Math.min(MAX_CLOUDS, Math.round(area / CLOUD_AREA)));
    this.clouds.position.y = floorY + 150; // a layer of sky above the floor (rises with the tower)
    this.cloudData.length = 0;
    for (let i = 0; i < this.cloudCount; i++) {
      const w = 300 + Math.random() * 260;
      this.cloudData.push({
        x: (Math.random() - 0.5) * 2 * this.cloudExtX,
        z: (Math.random() - 0.5) * 2 * this.cloudExtZ,
        w, h: w * (0.75 + Math.random() * 0.3), spin: Math.random() * Math.PI * 2,
      });
    }
    this.clouds.count = this.cloudCount;
    this.writeClouds();
  }

  private writeClouds(): void {
    if (!this.clouds) return;
    for (let i = 0; i < this.cloudCount; i++) {
      const d = this.cloudData[i];
      _ce.set(-Math.PI / 2, 0, d.spin); _cq.setFromEuler(_ce); // flat, with a per-cloud spin
      _cv.set(d.x, 0, d.z); _cs.set(d.w, d.h, 1);
      _cm.compose(_cv, _cq, _cs);
      this.clouds.setMatrixAt(i, _cm);
    }
    this.clouds.instanceMatrix.needsUpdate = true;
  }

  /** Drift the WORLD-FIXED clouds (real-time, fine while paused). They do NOT follow the player. */
  updateClouds(dt: number): void {
    if (!this.clouds || !this.cloudCount) return;
    const drift = 6 * dt;
    for (let i = 0; i < this.cloudCount; i++) {
      const d = this.cloudData[i];
      d.x += drift;
      if (d.x > this.cloudExtX) d.x -= 2 * this.cloudExtX; // toroidal wrap across the field
    }
    this.writeClouds();
  }

  /** Advance the day/night cycle from the game clock `now` (freezes on pause): lerp lights, fog, sky,
   *  and the sun's elevation (low at dawn/dusk → long shadows). Writes sunBase for syncSun to apply. */
  setTimeOfDay(now: number): void {
    const t = (now / DAY_LENGTH) % 1;
    let i = DAY.length - 1;
    for (let k = 0; k < DAY.length - 1; k++) if (t >= DAY[k].t && t < DAY[k + 1].t) { i = k; break; }
    const a = DAY[i], b = DAY[(i + 1) % DAY.length];
    let span = b.t - a.t; if (span <= 0) span += 1; // last→first wraps past 1.0
    let local = t - a.t; if (local < 0) local += 1;
    const f = smoothstep(Math.min(1, local / span));
    const lerp = (x: number, y: number) => x + (y - x) * f;
    this.sun.color.lerpColors(this._cA.setHex(a.sun), this._cB.setHex(b.sun), f);
    this.sun.intensity = lerp(a.sunI, b.sunI);
    this.ambient.color.lerpColors(this._cA.setHex(a.amb), this._cB.setHex(b.amb), f);
    this.ambient.intensity = lerp(a.ambI, b.ambI);
    this.hemi.color.lerpColors(this._cA.setHex(a.hemiS), this._cB.setHex(b.hemiS), f);
    this.hemi.groundColor.lerpColors(this._cA.setHex(a.hemiG), this._cB.setHex(b.hemiG), f);
    this.hemi.intensity = lerp(a.hemiI, b.hemiI);
    const fog = this.scene.fog as THREE.Fog | null;
    if (fog) fog.color.lerpColors(this._cA.setHex(a.fog), this._cB.setHex(b.fog), f);
    if (this.cloudMat) this.cloudMat.color.lerpColors(this._cA.setHex(a.cloud), this._cB.setHex(b.cloud), f); // clouds pick up the sky mood (dim at night)
    // sky gradient — only redraw the canvas when the rounded colors actually change
    this._cA.setHex(a.skyTop); this._cB.setHex(b.skyTop); const top = this._cA.lerp(this._cB, f).getHex();
    this._cA.setHex(a.skyBot); this._cB.setHex(b.skyBot); const bot = this._cA.lerp(this._cB, f).getHex();
    if (top !== this.lastSkyTop || bot !== this.lastSkyBot) { this.setSky(top, bot); this.lastSkyTop = top; this.lastSkyBot = bot; }
    // sun elevation → position (low = long shadows) with a fixed azimuth; bias more at grazing angles
    const elev = lerp(a.elev, b.elev), ce = Math.cos(elev), se = Math.sin(elev), R = 300;
    this.sunBase.set(SUN_HDIR.x * ce * R, se * R + 40, SUN_HDIR.y * ce * R);
    this.sun.shadow.normalBias = 0.8 + (1 - se) * 1.2;
  }

  /** Camera offset from the focus for a given orbit yaw + zoom. */
  private offset(yaw: number, zoom: number): THREE.Vector3 {
    const back = CAMERA.back * zoom;
    return this.want.set(Math.sin(yaw) * back, CAMERA.height * zoom, Math.cos(yaw) * back);
  }

  /** Push the horizon fog out proportionally to the zoom — otherwise, when zoomed far
   *  out, the camera sits past the (fixed) fog distance and the whole arena greys out. */
  private zoomFog(zoom: number): void {
    const f = this.scene.fog as THREE.Fog | null;
    if (f) { f.near = 460 * zoom; f.far = 1040 * zoom; }
  }

  /** Snap the camera straight to its target pose (used on layer entry / start). */
  snapTo(focus: THREE.Vector3, yaw = 0, zoom = 1): void {
    this.camera.position.copy(focus).add(this.offset(yaw, zoom));
    this.camAim.copy(focus);
    this.camera.lookAt(this.camAim);
    this.zoomFog(zoom);
    this.syncSun(focus);
  }

  /** Smoothly follow the focus point (the player) from above, at orbit yaw + zoom. */
  follow(focus: THREE.Vector3, dt: number, yaw = 0, zoom = 1): void {
    const want = this.offset(yaw, zoom).add(focus);
    const k = 1 - Math.exp(-CAMERA.lerp * dt);
    this.camera.position.lerp(want, k);
    this.camAim.lerp(focus, k);
    this.camera.lookAt(this.camAim);
    this.zoomFog(zoom);
    this.syncSun(focus);
  }

  private syncSun(focus: THREE.Vector3): void {
    this.sun.position.copy(focus).add(this.sunBase); // sunBase set by setTimeOfDay (elevation/azimuth)
    this.sun.target.position.copy(focus);
    this.sun.target.updateMatrixWorld();
  }

  /** Raycast a screen point (NDC) onto a horizontal plane at height y. */
  screenToGround(ndcX: number, ndcY: number, y: number, out: THREE.Vector3): boolean {
    const ray = this.ray;
    ray.setFromCamera(this.ndc.set(ndcX, ndcY), this.camera);
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-5) return false;
    const t = (y - ray.ray.origin.y) / dir.y;
    if (t < 0) return false;
    out.copy(ray.ray.origin).addScaledVector(dir, t);
    return true;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private onResize = () => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };
}
